import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../config/firebase';
import {
  isAdminClaim,
  signOutAdmin,
} from '../services/AuthService';
import {
  clearMfaSession,
  isAdminMfaEnrolled,
  isMfaSessionVerified,
  markMfaSessionVerified,
} from '../services/MfaService';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  mfaEnrolled: boolean;
  mfaVerified: boolean;
  refreshAuthState: () => Promise<void>;
  completeMfaVerification: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);

  const applyUserState = useCallback(async (nextUser: User | null) => {
    if (!nextUser) {
      setUser(null);
      setIsAdmin(false);
      setMfaEnrolled(false);
      setMfaVerified(false);
      return;
    }

    const admin = await isAdminClaim(nextUser);
    if (!admin) {
      clearMfaSession();
      await signOutAdmin();
      setUser(null);
      setIsAdmin(false);
      setMfaEnrolled(false);
      setMfaVerified(false);
      return;
    }

    const enrolled = await isAdminMfaEnrolled(nextUser.uid);
    const verified = enrolled && isMfaSessionVerified(nextUser.uid);

    setUser(nextUser);
    setIsAdmin(true);
    setMfaEnrolled(enrolled);
    setMfaVerified(verified);
  }, []);

  const refreshAuthState = useCallback(async () => {
    await applyUserState(auth.currentUser);
  }, [applyUserState]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      setLoading(true);
      await applyUserState(nextUser);
      setLoading(false);
    });

    return unsubscribe;
  }, [applyUserState]);

  const completeMfaVerification = useCallback(() => {
    if (!user) return;
    markMfaSessionVerified(user.uid);
    setMfaVerified(true);
  }, [user]);

  const logout = useCallback(async () => {
    clearMfaSession();
    await signOutAdmin();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAdmin,
      mfaEnrolled,
      mfaVerified,
      refreshAuthState,
      completeMfaVerification,
      logout,
    }),
    [
      user,
      loading,
      isAdmin,
      mfaEnrolled,
      mfaVerified,
      refreshAuthState,
      completeMfaVerification,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth yalnızca AuthProvider içinde kullanılabilir.');
  }
  return context;
};
